import React, { useState, useEffect, useRef } from 'react';
import { Search, Plus, Clipboard, Trash2, Tag, X, Download, Upload, HelpCircle, ExternalLink, PlusCircle, Home, Check, Globe, Lock, Sparkles, RefreshCw, LogOut, LogIn } from 'lucide-react';
import DOMPurify from 'dompurify';
import VaultSwitcher from './VaultSwitcher';
import Logo from './Logo';
import OptimizedImage from './OptimizedImage';
import AsyncCodeBlock from './AsyncCodeBlock';
import { minifyExpression } from '@snipit/core/utils/formatters';
import { DEV_LANGUAGES, AI_LANGUAGES, LINK_TYPES, TOOL_TYPES, INITIAL_SNIPPET } from '@snipit/core/utils/constants';
import { useAuth } from '@snipit/core/context/AuthContext';

const HomeView = ({
  isWebDashboard,
  snippets, setSnippets, setView, searchRef, setNewSnippet, handleDelete, handleTogglePublic,
  currentVault, setCurrentVault, devTabs, setDevTabs, aiTabs, setAiTabs,
  linkTabs, setLinkTabs, toolTabs, setToolTabs, theme, onOpenDashboard, addSnippet, fetchSnippetContent, handleDeleteBulk,
  onCodeReview, userLicense, onSync
}) => {
  const { userProfile, logout, loginWithGoogle } = useAuth() || {};
  const [query, setQuery] = useState('');
  const [activeTab, setActiveTab] = useState('All');
  const [exportSuccess, setExportSuccess] = useState(false);
  const [activeTag, setActiveTag] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedSnippets, setSelectedSnippets] = useState(new Set());
  const [selectedSnippetDetails, setSelectedSnippetDetails] = useState(null);
  const [expandedSnippets, setExpandedSnippets] = useState({});
  const [showDetailPreview, setShowDetailPreview] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const fileInputRef = useRef(null);

  // Map tabs and languages based on vault
  const activeTabs = currentVault === 'dev' ? devTabs : currentVault === 'ai' ? aiTabs : currentVault === 'tools' ? toolTabs : linkTabs;
  const availableLangs = currentVault === 'dev' ? DEV_LANGUAGES : currentVault === 'ai' ? AI_LANGUAGES : currentVault === 'tools' ? TOOL_TYPES : LINK_TYPES;
  const vaultSnippets = snippets.filter(s => (s.vault || 'dev') === currentVault);
  const allTags = Array.from(new Set(vaultSnippets.flatMap(s => s.tags || []))).sort();

  const filtered = vaultSnippets.filter(s => {
    const matchesQuery = s.title.toLowerCase().includes(query.toLowerCase()) ||
      (s.content && s.content.toLowerCase().includes(query.toLowerCase())) ||
      (s.url && s.url.toLowerCase().includes(query.toLowerCase()));
    const matchesTab = activeTab === 'All' || s.type === activeTab;
    const matchesTag = !activeTag || (s.tags && s.tags.includes(activeTag));
    return matchesQuery && matchesTab && matchesTag;
  });

  useEffect(() => {
    setActiveTab('All');
    setActiveTag(null);
    setSelectedSnippets(new Set());
  }, [currentVault]);

  const pasteToPage = async (snippet, overrideText = null) => {
    let content = overrideText !== null ? overrideText : snippet.content;
    if (fetchSnippetContent && overrideText === null && !content) {
      const data = await fetchSnippetContent(snippet.id);
      if (data) content = data.content;
    }
    const text = (snippet.type === 'AppSheet' || snippet.type === 'Sheets') ? minifyExpression(content || '') : (content || '');

    // Desktop & Web App Fallback
    if (!(window.chrome && window.chrome.tabs)) {
      try {
        await navigator.clipboard.writeText(text);
        alert("Copied to clipboard!");
      } catch (err) {
        console.error("Failed to copy", err);
        alert("Failed to copy to clipboard.");
      }
      return;
    }

    // Chrome Extension Flow
    const [tab] = await chrome.tabs.query({ active: true, currentWindow: true });
    if (!tab) return alert("Could not find an active web page!");

    try { await navigator.clipboard.writeText(text); } catch (err) { console.error("Failed to copy", err); }
    chrome.tabs.sendMessage(tab.id, { type: "PASTE_SNIPPET", text }, (response) => {
      if (chrome.runtime.lastError) alert("Copied to clipboard! \n\n(Tip: Refresh the webpage if you want direct insertion to work).");
      else if (response && response.success === false) alert("Copied to clipboard! 📋\n\nThis website uses a complex editor (like Google Docs). Just press Ctrl+V to paste your snippet now.");
    });
  };

  const addTab = (lang) => {
    if (currentVault === 'dev' && !devTabs.includes(lang)) {
      setDevTabs([...devTabs, lang]); chrome.storage.local.set({ devTabs: [...devTabs, lang] });
    } else if (currentVault === 'ai' && !aiTabs.includes(lang)) {
      setAiTabs([...aiTabs, lang]); chrome.storage.local.set({ aiTabs: [...aiTabs, lang] });
    } else if (currentVault === 'tools' && !toolTabs.includes(lang)) {
      setToolTabs([...toolTabs, lang]); chrome.storage.local.set({ toolTabs: [...toolTabs, lang] });
    } else if (currentVault === 'links' && !linkTabs.includes(lang)) {
      setLinkTabs([...linkTabs, lang]); chrome.storage.local.set({ linkTabs: [...linkTabs, lang] });
    }
    setActiveTab(lang);
    setShowDropdown(false);
  };

  const removeTab = (lang, e) => {
    e.stopPropagation();
    if (currentVault === 'dev') {
      const newTabs = devTabs.filter(t => t !== lang); setDevTabs(newTabs); chrome.storage.local.set({ devTabs: newTabs });
    } else if (currentVault === 'ai') {
      const newTabs = aiTabs.filter(t => t !== lang); setAiTabs(newTabs); chrome.storage.local.set({ aiTabs: newTabs });
    } else if (currentVault === 'tools') {
      const newTabs = toolTabs.filter(t => t !== lang); setToolTabs(newTabs); chrome.storage.local.set({ toolTabs: newTabs });
    } else {
      const newTabs = linkTabs.filter(t => t !== lang); setLinkTabs(newTabs); chrome.storage.local.set({ linkTabs: newTabs });
    }
    if (activeTab === lang) setActiveTab('All');
  };

  const editSnippet = async (snippet) => {
    try {
      let contentData = {};
      if (fetchSnippetContent && !snippet.content) {
        const data = await fetchSnippetContent(snippet.id);
        if (data) contentData = data;
      }
      setNewSnippet({ ...INITIAL_SNIPPET, ...snippet, ...contentData, tags: snippet.tags || [] });
      setView('create');
    } catch (err) {
      console.error("Failed to load snippet:", err);
      alert("Failed to load snippet content: " + err.message);
    }
  };

  const handleExport = () => {
    if (snippets.length === 0) return alert("No snippets to export!");
    const exportData = snippets.map(s => {
      if (['AppSheet', 'Sheets'].includes(s.type)) return { ...s, content: minifyExpression(s.content) };
      return s;
    });

    const fileContent = JSON.stringify(exportData, null, 2);
    const defaultFilename = `SnipIT_Backup_${new Date().toISOString().split('T')[0]}.json`;

    // Seamless Enterprise Download Flow via Blob URL
    const blob = new Blob([fileContent], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute("href", url);
    downloadAnchorNode.setAttribute("download", defaultFilename);
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    document.body.removeChild(downloadAnchorNode);

    setExportSuccess(true);
    setTimeout(() => setExportSuccess(false), 2000);
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };

  const handleImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const fileReader = new FileReader();
    fileReader.readAsText(file, "UTF-8");

    fileReader.onload = (e) => {
      try {
        const rawText = e.target.result.replace(/\r?\n|\r/g, "");
        const importedData = JSON.parse(rawText);
        if (!Array.isArray(importedData)) throw new Error("Invalid format");

        const existingIds = new Set(snippets.map(s => s.id));
        const newSnippets = importedData.filter(s => s.id && !existingIds.has(s.id)).map(s => {
          if (!s.vault) s.vault = 'dev';
          if (s.content && ['AppSheet', 'Sheets'].includes(s.type)) s.content = minifyExpression(s.content);
          return s;
        });

        const combinedSnippets = [...snippets, ...newSnippets];

        if (addSnippet) {
          Promise.all(newSnippets.map(async s => {
            const { id, ...snippetData } = s;
            await addSnippet(snippetData);
          }))
            .then(() => alert(`Success! Imported ${newSnippets.length} new items.`))
            .catch(err => {
              console.error("Import error:", err);
              alert("Import encountered an error. Please try again.");
            });
        } else if (setSnippets && window.chrome && window.chrome.storage) {
          chrome.storage.local.set({ snippets: combinedSnippets }, () => {
            setSnippets(combinedSnippets);
            alert(`Success! Imported ${newSnippets.length} new items.`);
          });
        }
      } catch (error) {
        alert("Failed to import. Ensure you selected a valid SnipIT backup file.");
      }
      event.target.value = null;
    };
  };

  return (
    <>
      <div className="flex justify-between mb-4 items-center shrink-0">
        <div className="flex items-center">
          {!isWebDashboard && (
            <h1 className="text-white font-bold text-lg flex items-center">
              <Logo vault={currentVault} className={`mr-2 w-7 h-7 transition-transform duration-300`} />
              Snip
              <span className={`transition-colors duration-300 font-extrabold ${currentVault === 'dev' ? 'text-transparent bg-clip-text bg-linear-to-r from-blue-400 to-cyan-400' :
                currentVault === 'ai' ? 'text-transparent bg-clip-text bg-linear-to-r from-purple-400 to-fuchsia-400' :
                  currentVault === 'links' ? 'text-transparent bg-clip-text bg-linear-to-r from-emerald-400 to-teal-400' :
                    currentVault === 'tools' ? 'text-transparent bg-clip-text bg-linear-to-r from-amber-400 to-orange-400' :
                      theme.textPrimary
                }`}>IT</span>
            </h1>
          )}
          {isWebDashboard && (
            <div className="w-8 h-8"></div> /* Spacer so layout doesn't break if dependent on flex */
          )}
        </div>

        <div className="flex items-center gap-2">
          {!isWebDashboard && userProfile && (
            <>
              <div className="flex items-center space-x-2 mr-2">
                {userProfile.photoURL ? (
                  <OptimizedImage src={userProfile.photoURL.replace('=s96-c', '=s32-c')} alt="Profile" className="w-7 h-7 rounded-full border border-gray-700" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-gray-700 flex items-center justify-center border border-gray-600">
                    <span className="text-xs font-bold">{userProfile.displayName?.charAt(0) || 'U'}</span>
                  </div>
                )}
                <div className="hidden sm:block">
                  <p className="text-xs font-medium text-gray-200 truncate max-w-20 leading-tight">{userProfile.displayName}</p>
                  <p className="text-[9px] text-blue-400 font-bold leading-tight capitalize">{userLicense?.tier || 'Free'} Tier</p>
                </div>
              </div>

              <button
                onClick={logout}
                className="p-1.5 rounded text-gray-400 hover:text-red-400 hover:bg-gray-800 transition mr-1"
                title="Logout"
              >
                <LogOut size={16} />
              </button>
              <div className="w-px h-5 bg-gray-700 mx-1"></div>
            </>
          )}

          {!isWebDashboard && !userProfile && (
            <button
              onClick={async () => {
                setLoginLoading(true);
                try {
                  await loginWithGoogle();
                } finally {
                  setLoginLoading(false);
                }
              }}
              disabled={loginLoading}
              className={`px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded shadow transition-colors flex items-center gap-1.5 ${loginLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {loginLoading ? <RefreshCw size={14} className="animate-spin" /> : <LogIn size={14} />}
              {loginLoading ? 'Processing...' : 'Login / Upgrade'}
            </button>
          )}

          {!isWebDashboard && (
            <button
              onClick={() => {
                if (onOpenDashboard) {
                  onOpenDashboard();
                } else if (typeof window !== 'undefined' && !window.__TAURI__ && !(window.chrome && window.chrome.runtime && window.chrome.runtime.id)) {
                  window.location.href = '/';
                } else {
                  window.open('https://www.snipit.devitsystems.com', '_blank');
                }
              }}
              className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-gray-800 transition"
              title="Web Dashboard"
            >
              <Home size={18} />
            </button>
          )}
          {!isWebDashboard && (
            <button onClick={() => setView('help')} className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-gray-800 transition" title="Help & Documentation">
              <HelpCircle size={18} />
            </button>
          )}


          {!isWebDashboard && (
            <>
              <div className="w-px h-5 bg-gray-700 mx-1"></div>
              <button aria-label="Add new item" onClick={() => { setNewSnippet({ ...INITIAL_SNIPPET, type: currentVault === 'dev' ? 'AppSheet' : currentVault === 'ai' ? 'Prompt' : currentVault === 'tools' ? 'JWT Decoder' : 'GitHub Repo', vault: currentVault }); setView('create'); }} className={`${theme.bgPrimary} w-8 h-8 flex items-center justify-center rounded-full text-white ${theme.bgHover} transition shadow-md ${theme.shadowPrimary}`}>
                <Plus size={18} />
              </button>
            </>
          )}
        </div>
      </div>

      <VaultSwitcher currentVault={currentVault} setCurrentVault={setCurrentVault} />

      <div className="relative mb-3 shrink-0">
        <label htmlFor="search-input" className="sr-only">Search Items</label>
        <Search className="absolute left-2.5 top-2.5 text-gray-500" size={14} />
        <input
          id="search-input" name="searchQuery"
          ref={searchRef} className="w-full bg-surface p-2 pl-8 text-sm rounded outline-none border border-gray-800 focus:border-gray-600 transition-colors"
          placeholder={`Search ${currentVault === 'dev' ? 'snippets' : currentVault === 'ai' ? 'prompts' : currentVault === 'tools' ? 'tools' : 'links'}... (Ctrl+K)`}
          value={query} onChange={e => setQuery(e.target.value)} autoComplete="off"
        />
      </div>

      <div className="flex items-center gap-2 mb-3 border-b border-gray-800 pb-2 shrink-0">
        <div className="flex gap-4 overflow-x-auto no-scrollbar text-sm grow">
          {activeTabs.map(tab => (
            <button
              key={tab} onClick={() => setActiveTab(tab)}
              className={`whitespace-nowrap flex items-center gap-1 group ${activeTab === tab ? `${theme.textPrimary} font-bold` : 'text-gray-400'} hover:text-gray-300 transition-colors`}
            >
              {tab}
              {tab !== 'All' && (
                <X size={12} className="opacity-0 group-hover:opacity-100 hover:text-red-400 transition-opacity" onClick={(e) => removeTab(tab, e)} />
              )}
            </button>
          ))}
        </div>

        <div className="relative flex items-center gap-2 shrink-0 ml-2">
          {(!userLicense || userLicense.tier === 'free') ? (
            <div className="flex gap-1 mr-1">
              <button onClick={handleExport} disabled={exportSuccess} className="w-6 h-6 flex items-center justify-center rounded-md text-gray-400 hover:text-white hover:bg-gray-800 transition" title="Export Backup">
                {exportSuccess ? <Check className="text-green-400" size={14} /> : <Download size={14} />}
              </button>
              <label htmlFor="import-backup" className="sr-only">Import Backup</label>
              <button onClick={() => fileInputRef.current?.click()} className="w-6 h-6 flex items-center justify-center rounded-md text-gray-400 hover:text-white hover:bg-gray-800 transition" title="Import Backup">
                <Upload size={14} />
              </button>
              <input id="import-backup" name="importBackup" type="file" accept=".json" ref={fileInputRef} style={{ display: 'none' }} onChange={handleImport} />
            </div>
          ) : (
            <button onClick={async () => {
              if (onSync) {
                setSyncLoading(true);
                try {
                  await onSync();
                } finally {
                  setSyncLoading(false);
                }
              }
              else alert("Vault synced with cloud!");
            }}
              disabled={syncLoading}
              className={`w-6 h-6 mr-1 flex items-center justify-center rounded-md text-indigo-400 hover:text-white hover:bg-indigo-900/30 transition ${syncLoading ? 'opacity-50 cursor-not-allowed' : ''}`} title="Sync to Cloud">
              <RefreshCw size={14} className={syncLoading ? "animate-spin" : ""} />
            </button>
          )}

          <button onClick={() => setShowDropdown(!showDropdown)} className={`text-gray-400 hover:text-white transition-colors flex items-center gap-1 text-xs font-semibold bg-surface px-2 py-1 rounded border border-gray-800`}>
            <PlusCircle size={14} /> Tab
          </button>

          {isWebDashboard && (
            <button 
              aria-label="Add new item" 
              onClick={() => { setNewSnippet({ ...INITIAL_SNIPPET, type: currentVault === 'dev' ? 'AppSheet' : currentVault === 'ai' ? 'Prompt' : currentVault === 'tools' ? 'JWT Decoder' : 'GitHub Repo', vault: currentVault }); setView('create'); }} 
              className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-bold text-white transition-all duration-200 shadow-md hover:shadow-lg hover:-translate-y-0.5 ${currentVault === 'dev' ? 'bg-blue-600 hover:bg-blue-500 shadow-blue-500/25' : currentVault === 'ai' ? 'bg-purple-600 hover:bg-purple-500 shadow-purple-500/25' : currentVault === 'tools' ? 'bg-amber-600 hover:bg-amber-500 shadow-amber-500/25' : 'bg-emerald-600 hover:bg-emerald-500 shadow-emerald-500/25'}`}
            >
              <Plus size={14} strokeWidth={3} /> <span className="hidden sm:inline">SnipIT</span>
            </button>
          )}

          {showDropdown && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowDropdown(false)} />
              <div className="absolute right-0 top-8 z-20 w-40 bg-surface border border-gray-800 rounded shadow-xl py-1 max-h-48 overflow-y-auto custom-scrollbar">
                {availableLangs.filter(lang => !activeTabs.includes(lang)).map(lang => (
                  <button
                    key={lang} onClick={() => addTab(lang)}
                    className={`w-full text-left px-3 py-1.5 text-xs text-gray-300 ${theme.bgHover} hover:text-white transition-colors`}
                  >
                    + {lang}
                  </button>
                ))}
                {availableLangs.filter(lang => !activeTabs.includes(lang)).length === 0 && (
                  <div className="px-3 py-1.5 text-xs text-gray-600 italic">All tabs added!</div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ALL TAGS (Horizontally scrollable) */}
      {allTags.length > 0 && (
        <div className="flex gap-2 mb-3 overflow-x-auto no-scrollbar shrink-0 px-1 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] scrollbar-none">
          {allTags.map(tag => (
            <button
              key={tag}
              onClick={() => setActiveTag(activeTag === tag ? null : tag)}
              className={`shrink-0 px-3 py-1 rounded-full text-[10px] font-bold tracking-wide uppercase transition-colors ${
                activeTag === tag 
                  ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20' 
                  : 'bg-surface border border-gray-800 text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
            >
              {tag.startsWith('#') ? tag : `#${tag}`}
            </button>
          ))}
        </div>
      )}


      {/* Bulk Selection Toolbar */}
      {filtered.length > 0 && (
        <div className="flex items-center gap-2 mb-2 px-1 shrink-0">
          <label className="flex items-center gap-1.5 cursor-pointer text-xs text-gray-400 hover:text-white transition-colors">
            <input
              type="checkbox"
              checked={selectedSnippets.size === filtered.length && filtered.length > 0}
              onChange={(e) => {
                if (e.target.checked) setSelectedSnippets(new Set(filtered.map(s => s.id)));
                else setSelectedSnippets(new Set());
              }}
              className="accent-blue-600 cursor-pointer"
              aria-label="Select All Snippets"
            />
            Select All ({filtered.length})
          </label>

          <button
            onClick={() => {
              const seen = new Set();
              const duplicates = new Set();

              // Sort by createdAt so we keep the oldest one
              const sorted = [...filtered].sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''));

              sorted.forEach(s => {
                const signature = `${s.type}-${s.title.toLowerCase()}-${s.contentPreview || ''}`;
                if (seen.has(signature)) {
                  duplicates.add(s.id);
                } else {
                  seen.add(signature);
                }
              });

              setSelectedSnippets(duplicates);
              if (duplicates.size === 0) alert("No duplicates found in the current view!");
            }}
            className="text-[10px] text-gray-400 hover:text-white bg-surface hover:bg-gray-800 px-2 py-1 rounded border border-gray-800 transition-colors"
          >
            Select Duplicates
          </button>

          {selectedSnippets.size > 0 && (
            <button
              onClick={async () => {
                if (window.confirm(`Are you sure you want to delete ${selectedSnippets.size} items?`)) {
                  if (handleDeleteBulk) {
                    await handleDeleteBulk(Array.from(selectedSnippets));
                    setSelectedSnippets(new Set());
                  }
                }
              }}
              className="ml-auto text-[10px] bg-red-900/30 text-red-400 hover:bg-red-900/50 hover:text-red-300 px-2 py-1 rounded font-semibold transition-colors"
            >
              Delete Selected ({selectedSnippets.size})
            </button>
          )}
        </div>
      )}

      <div className="grow overflow-y-auto space-y-3 pr-1 custom-scrollbar">
        {filtered.length === 0 ? (
          <div className="text-center text-gray-600 mt-10 text-sm">No items found in this vault.</div>
        ) : (
          filtered.map(s => (
            <div key={s.id} className={`p-3 ${theme.bgSubtle} backdrop-blur-md rounded border transition-colors flex flex-col ${selectedSnippets.has(s.id) ? 'border-blue-500/50' : `${theme.borderSubtle} hover:border-gray-500`} shadow-lg ${theme.shadowPrimary} group`}>

              <div className="flex justify-between items-start mb-2 cursor-pointer" onClick={async () => {
                let contentData = {};
                if (fetchSnippetContent && !s.content) {
                  const data = await fetchSnippetContent(s.id, s.userId);
                  if (data) contentData = data;
                }
                setSelectedSnippetDetails({ ...s, ...contentData });
                setShowDetailPreview(false);
              }}>
                <div className="flex items-start gap-2 pr-2 overflow-hidden">
                  <div className="pt-0.5 shrink-0" onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedSnippets.has(s.id)}
                      onChange={(e) => {
                        const newSet = new Set(selectedSnippets);
                        if (e.target.checked) newSet.add(s.id);
                        else newSet.delete(s.id);
                        setSelectedSnippets(newSet);
                      }}
                      className="accent-blue-600 cursor-pointer"
                      aria-label={`Select ${s.title || 'Snippet'}`}
                    />
                  </div>
                  <div className="flex flex-col truncate">
                    <span className={`text-sm font-semibold text-white group-hover:${theme.textPrimary} transition-colors truncate`}>{s.title}</span>
                    {s.contentPreview && (
                      <span className="text-xs text-gray-500 truncate mt-0.5">{s.contentPreview}</span>
                    )}
                    {s.tags && s.tags.length > 0 && (
                      <div className="flex gap-1 mt-1 flex-wrap">
                        {s.tags.map(t => (
                          <span key={t} className={`text-[9px] px-1.5 py-0.5 rounded font-mono ${theme.bgSubtle} ${theme.textPrimary}`}>
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {s.url && (
                <div className="text-xs text-gray-400 truncate mb-2 cursor-pointer hover:underline" onClick={() => window.open(s.url.startsWith('http') ? s.url : `https://${s.url}`, '_blank')}>
                  {s.url}
                </div>
              )}

              {/* Special Renderer for JWT Decoder */}
              {s.vault === 'tools' && s.type === 'JWT Decoder' ? (
                <div className="bg-black p-2 rounded overflow-auto border border-gray-900 h-28 min-h-15 max-h-[80vh] resize-y custom-scrollbar">
                  <pre className="m-0 text-[10px] text-emerald-400 font-mono">
                    {s.toolOutput || 'No decoded payload available.'}
                  </pre>
                </div>
              ) : s.vault === 'tools' && s.type === 'Regex Tester' ? (
                <div className="flex flex-col gap-2">
                  {s.pattern && (
                    <div className="bg-surface px-2 py-1.5 rounded border border-amber-900/50 text-amber-400 font-mono text-xs overflow-x-auto custom-scrollbar whitespace-nowrap">
                      {s.outputFormat === 'Literal' ? <><span className="text-gray-500 mr-2 select-none">/</span>{s.pattern}<span className="text-gray-500 ml-2 select-none">/g</span></> :
                        s.outputFormat === 'String' ? <><span className="text-gray-500 mr-2 select-none">"</span>{s.pattern}<span className="text-gray-500 ml-2 select-none">"</span></> :
                          s.outputFormat === 'RegExp' ? <><span className="text-gray-500 mr-2 select-none">new RegExp("</span>{s.pattern}<span className="text-gray-500 ml-2 select-none">", "g")</span></> :
                            <>{s.pattern}</>}
                    </div>
                  )}
                  {s.content && (
                    <div className="bg-black p-2 rounded overflow-auto border border-gray-900 h-16 min-h-10 max-h-[40vh] resize-y custom-scrollbar text-[10px] text-gray-400 font-mono whitespace-pre-wrap">
                      {s.content}
                    </div>
                  )}
                </div>
              ) : s.content && (
                expandedSnippets[s.id] ? (
                  <div className="bg-black p-2 rounded overflow-auto border border-gray-900 h-28 min-h-15 max-h-[80vh] resize-y custom-scrollbar relative">
                    <button 
                      onClick={(e) => { e.stopPropagation(); setExpandedSnippets(prev => ({...prev, [s.id]: false})) }}
                      className="absolute top-2 right-2 bg-gray-800/80 backdrop-blur text-gray-300 px-2 py-1 text-[9px] font-bold rounded hover:bg-gray-700 transition"
                    >Hide</button>
                    <pre className="m-0 text-[10px] mt-1">
                      {/* CRITICAL: Maps specific Tools to PrismJS syntax highlighting */}
                      <AsyncCodeBlock
                        className={`language-${s.type === 'JSON Formatter' ? 'json' : s.type === 'Boilerplate Gen' ? 'javascript' : s.type.toLowerCase()}`}
                        code={s.content}
                        type={s.type === 'JSON Formatter' ? 'JSON' : s.type === 'Boilerplate Gen' ? 'Javascript' : s.type}
                      />
                    </pre>
                  </div>
                ) : (
                  <div 
                    onClick={(e) => { e.stopPropagation(); setExpandedSnippets(prev => ({...prev, [s.id]: true})) }}
                    className="bg-black/50 p-2 rounded border border-gray-900 cursor-pointer hover:bg-gray-900 hover:border-gray-700 transition-colors flex items-center justify-center h-10"
                  >
                    <span className="text-[10px] text-gray-400 font-bold flex items-center gap-1.5">
                      <Sparkles size={12} /> Show Code Preview
                    </span>
                  </div>
                )
              )}
            </div>
          ))
        )}
      </div>

      {selectedSnippetDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200" onClick={() => setSelectedSnippetDetails(null)}>
          <div className={`w-full max-w-2xl max-h-[90vh] flex flex-col rounded-xl border ${theme.borderSubtle} ${theme.bgSubtle} backdrop-blur-md shadow-2xl overflow-hidden`} onClick={e => e.stopPropagation()}>
            <div className={`p-4 border-b ${theme.borderSubtle} flex justify-between items-center`}>
              <h2 className={`text-lg font-bold text-white`}>{selectedSnippetDetails.title}</h2>
              <button onClick={() => setSelectedSnippetDetails(null)} className="text-gray-400 hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
              {selectedSnippetDetails.url && (
                <div className="text-sm text-gray-400 truncate mb-4 cursor-pointer hover:underline" onClick={() => window.open(selectedSnippetDetails.url.startsWith('http') ? selectedSnippetDetails.url : `https://${selectedSnippetDetails.url}`, '_blank')}>
                  {selectedSnippetDetails.url}
                </div>
              )}

              {selectedSnippetDetails.vault === 'tools' && selectedSnippetDetails.type === 'JWT Decoder' ? (
                <div className="bg-black p-4 rounded overflow-auto border border-gray-900 shadow-inner custom-scrollbar">
                  <pre className="m-0 text-xs text-emerald-400 font-mono whitespace-pre-wrap">
                    {selectedSnippetDetails.toolOutput || 'No decoded payload available.'}
                  </pre>
                </div>
              ) : selectedSnippetDetails.vault === 'tools' && selectedSnippetDetails.type === 'Regex Tester' ? (
                <div className="flex flex-col gap-3">
                  {selectedSnippetDetails.pattern && (
                    <div className="bg-surface px-3 py-2 rounded border border-amber-900/50 text-amber-400 font-mono text-sm overflow-x-auto custom-scrollbar whitespace-nowrap">
                      {selectedSnippetDetails.outputFormat === 'Literal' ? <><span className="text-gray-500 mr-2 select-none">/</span>{selectedSnippetDetails.pattern}<span className="text-gray-500 ml-2 select-none">/g</span></> :
                        selectedSnippetDetails.outputFormat === 'String' ? <><span className="text-gray-500 mr-2 select-none">"</span>{selectedSnippetDetails.pattern}<span className="text-gray-500 ml-2 select-none">"</span></> :
                          selectedSnippetDetails.outputFormat === 'RegExp' ? <><span className="text-gray-500 mr-2 select-none">new RegExp("</span>{selectedSnippetDetails.pattern}<span className="text-gray-500 ml-2 select-none">", "g")</span></> :
                            <>{selectedSnippetDetails.pattern}</>}
                    </div>
                  )}
                  {selectedSnippetDetails.content && (
                    <div className="bg-black p-4 rounded overflow-auto border border-gray-900 min-h-25 shadow-inner custom-scrollbar text-xs text-gray-400 font-mono whitespace-pre-wrap">
                      {selectedSnippetDetails.content}
                    </div>
                  )}
                </div>
              ) : selectedSnippetDetails.type === 'Markdown Pad' ? (
                <div className="flex flex-col gap-2">
                  <div className="flex justify-between items-center mb-1">
                    <label className={`text-[10px] font-bold ${theme.textPrimary} uppercase tracking-wider`}>{!showDetailPreview ? 'Raw Markdown' : 'Preview'}</label>
                    <button onClick={() => setShowDetailPreview(!showDetailPreview)} className={`text-[10px] px-2 py-1 rounded ${!showDetailPreview ? `${theme.bgPrimary} ${theme.bgHover}` : 'bg-gray-700 hover:bg-gray-600'} text-white font-bold transition-colors`}>
                      {!showDetailPreview ? 'Show Preview' : 'Back to Editor'}
                    </button>
                  </div>
                  {!showDetailPreview ? (
                    <div className="bg-black p-4 rounded overflow-auto border border-gray-900 shadow-inner custom-scrollbar min-h-25">
                      <pre className="m-0 text-xs text-gray-300 font-mono whitespace-pre-wrap">
                        {selectedSnippetDetails.content}
                      </pre>
                    </div>
                  ) : (
                    <div
                      className="bg-surface p-4 text-sm rounded border border-gray-800 shadow-inner overflow-auto custom-scrollbar prose prose-invert prose-sm min-h-25"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedSnippetDetails.toolOutput || '') }}
                    />
                  )}
                </div>
              ) : selectedSnippetDetails.type === 'Gem' ? (
                <div className="flex flex-col gap-4">
                  {(() => {
                    const sections = [];
                    const regex = /###\s+(Premise|Persona|Context|Rules)\s*\n([\s\S]*?)(?=(###\s+(?:Premise|Persona|Context|Rules)|$))/gi;
                    let match;
                    while ((match = regex.exec(selectedSnippetDetails.content)) !== null) {
                      sections.push({ title: match[1], content: match[2].trim() });
                    }
                    if (sections.length === 0) sections.push({ title: 'Content', content: selectedSnippetDetails.content.trim() });

                    return sections.map((sec, idx) => (
                      <div key={idx} className="flex flex-col gap-1">
                        <div className={`text-sm font-bold ${theme.textPrimary}`}>{sec.title}</div>
                        <div className="bg-black p-4 rounded overflow-auto border border-gray-900 shadow-inner custom-scrollbar relative min-h-20">
                          <pre className="m-0 text-xs whitespace-pre-wrap wrap-break-word text-gray-300">
                            <AsyncCodeBlock className="language-markdown" code={sec.content} type="Markdown" />
                          </pre>
                        </div>
                        <div className="flex justify-start mt-1 mb-2">
                          <button onClick={() => pasteToPage({ ...selectedSnippetDetails, content: sec.content }, sec.content)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold text-white ${theme.bgPrimary} ${theme.bgHover} shadow transition-colors`}>
                            <Clipboard size={14} /> Copy Code
                          </button>
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              ) : selectedSnippetDetails.content && (
                <div className="bg-black p-4 rounded overflow-auto border border-gray-900 shadow-inner custom-scrollbar">
                  <pre className={`m-0 text-xs ${selectedSnippetDetails.vault === 'ai' ? 'whitespace-pre-wrap wrap-break-word' : ''}`}>
                    <AsyncCodeBlock
                      className={`language-${selectedSnippetDetails.type === 'JSON Formatter' ? 'json' : selectedSnippetDetails.type === 'Boilerplate Gen' ? 'javascript' : selectedSnippetDetails.type.toLowerCase()}`}
                      code={selectedSnippetDetails.content}
                      type={selectedSnippetDetails.type === 'JSON Formatter' ? 'JSON' : selectedSnippetDetails.type === 'Boilerplate Gen' ? 'Javascript' : selectedSnippetDetails.type}
                    />
                  </pre>
                </div>
              )}
            </div>

            <div className={`p-3 bg-black/40 border-t ${theme.borderSubtle} flex justify-end items-center gap-2 shrink-0`}>
              {selectedSnippetDetails.url && (
                <button onClick={() => window.open(selectedSnippetDetails.url.startsWith('http') ? selectedSnippetDetails.url : `https://${selectedSnippetDetails.url}`, '_blank')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold ${theme.textPrimary} hover:text-white ${theme.bgSubtle} ${theme.bgHover} transition-colors`}>
                  <ExternalLink size={14} /> Open
                </button>
              )}
              {selectedSnippetDetails.type !== 'Gem' && (
                <button onClick={() => {
                  let overrideText = null;
                  if (selectedSnippetDetails.type === 'Regex Tester') {
                    if (selectedSnippetDetails.outputFormat === 'Literal') overrideText = `/${selectedSnippetDetails.pattern}/g`;
                    else if (selectedSnippetDetails.outputFormat === 'String') overrideText = `"${selectedSnippetDetails.pattern}"`;
                    else if (selectedSnippetDetails.outputFormat === 'RegExp') overrideText = `new RegExp("${selectedSnippetDetails.pattern}", "g")`;
                    else overrideText = selectedSnippetDetails.pattern;
                  }
                  pasteToPage(selectedSnippetDetails, overrideText);
                }} className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold text-white ${theme.bgPrimary} ${theme.bgHover} shadow-lg transition-colors`}>
                  <Clipboard size={14} /> Copy Code
                </button>
              )}
              {onCodeReview && currentVault === 'dev' && (userLicense?.tier === 'enterprise' || userLicense?.tier === 'enterprise_org') && (
                <button onClick={() => {
                  onCodeReview(selectedSnippetDetails);
                  setSelectedSnippetDetails(null);
                }} className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold ${theme.textPrimary} hover:text-white ${theme.bgSubtle} hover:bg-cyan-900/50 transition-colors`}>
                  <Sparkles size={14} /> AI Review
                </button>
              )}
              {(currentVault === 'dev' || currentVault === 'ai') && handleTogglePublic && userLicense?.tier !== 'free' && (
                <button onClick={() => {
                  handleTogglePublic(selectedSnippetDetails);
                  setSelectedSnippetDetails(prev => ({ ...prev, isPublic: !prev.isPublic }));
                }} className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold ${selectedSnippetDetails.isPublic ? 'text-blue-400 bg-blue-900/30 hover:bg-blue-900/50' : 'text-gray-400 bg-gray-800 hover:bg-gray-700'} transition-colors`}>
                  {selectedSnippetDetails.isPublic ? <Globe size={14} /> : <Lock size={14} />} {selectedSnippetDetails.isPublic ? "Public" : "Private"}
                </button>
              )}
              <button onClick={() => {
                editSnippet(selectedSnippetDetails);
                setSelectedSnippetDetails(null);
              }} className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold text-gray-300 hover:text-white bg-gray-800 hover:bg-gray-700 transition-colors`}>
                <Tag size={14} /> Edit
              </button>
              <button onClick={() => {
                handleDelete(selectedSnippetDetails.id);
                setSelectedSnippetDetails(null);
              }} className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold text-red-400 hover:text-red-300 bg-red-900/20 hover:bg-red-900/40 transition-colors">
                <Trash2 size={14} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HomeView;
